import { useEffect, useMemo, useState } from "react";
import { MAX_SAMPLES, etaMs, flowRateGpm, getDirection, toNumber } from "./transferMath";

const storageKey = (deviceId, captureId) => `tablet:${deviceId}:transfer:${captureId}`;

function readStored(deviceId, captureId) {
  try {
    const raw = sessionStorage.getItem(storageKey(deviceId, captureId));
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

/**
 * Tracks the open capture from live telemetry. Returns `{ status: "idle" }` when
 * no capture is open, and `status: "saving"` after the capture closes until the
 * saved transfer arrives (`savedTransferId`).
 */
export default function useTransferSession({ deviceId, deviceValue, deviceData, sensorOk, savedTransferId }) {
  const transfer = deviceData?.transfer ?? {};
  const captureId = transfer.active ? transfer.captureId ?? transfer.capture_id ?? null : null;
  const startedAt = transfer.startedAt ?? transfer.started_at ?? null;
  const gallons = toNumber(deviceValue?.volume_gallons);
  const recordedAt = deviceValue?.last_updated;
  const isStartEvent = deviceData?.reason === "transfer_started";

  const [session, setSession] = useState(null);
  const [completedSamples, setCompletedSamples] = useState({});

  useEffect(() => {
    if (!deviceId) return;
    const t = Date.parse(recordedAt) || Date.now();

    if (!captureId) {
      setSession((prev) => (prev && !prev.endedAt ? { ...prev, endedAt: t } : prev));
      return;
    }

    setSession((prev) => {
      let next = prev?.captureId === captureId ? prev : null;
      if (!next) {
        next = readStored(deviceId, captureId) ?? {
          captureId,
          startedAt,
          baseline: gallons,
          baselineExact: isStartEvent,
          samples: [],
          endedAt: null,
        };
      }
      if (!sensorOk || gallons == null) return next;

      const last = next.samples[next.samples.length - 1];
      if (last && last.t >= t) return next;

      return {
        ...next,
        baseline: next.baseline ?? gallons,
        samples: [...next.samples, { t, g: gallons }].slice(-MAX_SAMPLES),
        endedAt: null,
      };
    });
  }, [deviceId, captureId, recordedAt, gallons, sensorOk, startedAt, isStartEvent]);

  useEffect(() => {
    if (!session?.captureId) return;
    try {
      sessionStorage.setItem(storageKey(deviceId, session.captureId), JSON.stringify(session));
    } catch (error) {
      console.error("persist transfer session", error);
    }
  }, [deviceId, session]);

  // Keep the samples for the saved transfer so the monitor can still chart it.
  useEffect(() => {
    if (!savedTransferId || session?.captureId !== savedTransferId) return;
    setCompletedSamples((prev) => ({ ...prev, [savedTransferId]: session.samples }));
    sessionStorage.removeItem(storageKey(deviceId, savedTransferId));
    setSession(null);
  }, [deviceId, savedTransferId, session]);

  return useMemo(() => {
    if (!session) return { status: "idle", completedSamples };
    console.log('session',session);
    const current = gallons ?? session.samples[session.samples.length - 1]?.g ?? session.baseline;
    const moved = current != null && session.baseline != null ? current - session.baseline : null;
    const gpm = session.endedAt ? 0 : flowRateGpm(session.samples);

    return {
      ...session,
      status: session.endedAt ? "saving" : "active",
      gallons: current,
      moved,
      gpm,
      direction: getDirection({ pressureMode: deviceValue?.pressure_mode, baseline: session.baseline, gallons: current }),
      eta: etaMs({ gpm, gallons: current, capacity: deviceValue?.capacity_gallons }),
      completedSamples,
    };
  }, [session, gallons, deviceValue?.pressure_mode, deviceValue?.capacity_gallons, completedSamples]);
}
